import { Injectable } from '@angular/core'
import { Uuid } from '@ftr/contracts/type/shared'
import { AccessLevel } from '@ftr/distribution-contracts'
import { LocalDate, LocalTime } from '@js-joda/core'
import { Action, NgxsOnInit, Selector, State, StateContext } from '@ngxs/store'
import { patch, removeItem, updateItem } from '@ngxs/store/operators'
import { groupCartItems } from '../../utils'
import {
  AddCartItems,
  ClearCartItems,
  RemoveCartItem,
  SanitiseCartItems,
  SetCartCourtSystem,
  UpdateCartItem,
} from './cart.events'
import { CART_STATE_DEFAULT, CartGroup, CartItem, CartItemModel, CartStateModel, IdNamePair } from './cart.model'

@State<CartStateModel>({
  name: 'cart',
  defaults: CART_STATE_DEFAULT,
})
@Injectable()
export class CartState implements NgxsOnInit {
  ngxsOnInit(ctx: StateContext<CartStateModel>): void {
    ctx.dispatch(new SanitiseCartItems())
  }

  @Selector()
  static cartCourtSystem(state: CartStateModel): IdNamePair | undefined {
    return state.courtSystem
  }

  @Selector()
  static cartItems(state: CartStateModel): CartItem[] {
    return state.cartItems.map(mapCartItemModelToCartItem).filter(item => !!item)
  }

  @Selector()
  static sessionIds(state: CartStateModel): Set<Uuid> {
    return new Set(CartState.cartItems(state).map(({ sessionId }) => sessionId))
  }

  @Selector()
  static numCartItems(state: CartStateModel): number {
    return CartState.cartItems(state).length
  }

  @Selector()
  static groupedCartItems(state: CartStateModel): CartGroup[] {
    const items = CartState.cartItems(state)
    return groupCartItems(items)
  }

  @Selector()
  static isAllPublic(state: CartStateModel): boolean {
    const cartItems = CartState.cartItems(state)
    return cartItems.length > 0 && cartItems.every(cartItem => cartItem.rule.accessLevel === AccessLevel.Public)
  }

  @Action(SanitiseCartItems)
  sanitiseCartItems(ctx: StateContext<CartStateModel>): void {
    const state = ctx.getState()
    const safeItems = state.cartItems.filter(item => mapCartItemModelToCartItem(item))

    ctx.setState(patch({ cartItems: safeItems }))
  }

  @Action(AddCartItems)
  addCartItems(ctx: StateContext<CartStateModel>, { items }: AddCartItems): void {
    const incomingItemModels = items.map(mapCartItemToCartItemModel)
    const cartItems = [...ctx.getState().cartItems]

    for (const itemModel of incomingItemModels) {
      const existingIndex = cartItems.findIndex(item => item.sessionId === itemModel.sessionId)
      if (existingIndex >= 0) {
        cartItems[existingIndex] = itemModel
      } else {
        cartItems.push(itemModel)
      }
    }

    ctx.setState(patch({ cartItems }))
  }

  @Action(RemoveCartItem)
  removeCartItem(ctx: StateContext<CartStateModel>, { itemId }: RemoveCartItem): void {
    ctx.setState(
      patch({
        cartItems: removeItem<CartItemModel>(item => item.id === itemId),
      }),
    )
  }

  @Action(UpdateCartItem)
  updateCartItem(ctx: StateContext<CartStateModel>, { itemId, updates }: UpdateCartItem): void {
    const existingModel = ctx.getState().cartItems.find(i => i.id === itemId)
    if (!existingModel) return

    const existingItem = mapCartItemModelToCartItem(existingModel)
    if (!existingItem) return

    const cartItemModel = mapCartItemToCartItemModel({ ...existingItem, ...updates })
    ctx.setState(
      patch({
        cartItems: updateItem<CartItemModel>(existing => existing.id === itemId, cartItemModel),
      }),
    )
  }

  @Action(ClearCartItems)
  clearCartItems(ctx: StateContext<CartStateModel>): void {
    ctx.patchState({ cartItems: [] })
  }

  @Action(SetCartCourtSystem)
  setCourtSystemId(ctx: StateContext<CartStateModel>, { courtSystemId, courtSystemName }: SetCartCourtSystem): void {
    ctx.setState(patch<CartStateModel>({ courtSystem: { id: courtSystemId, name: courtSystemName } }))
  }
}

function mapCartItemModelToCartItem(cartItemModel: CartItemModel): CartItem | undefined {
  try {
    return {
      ...cartItemModel,
      recordingDate: LocalDate.parse(cartItemModel.recordingDate),
      startTime: LocalTime.parse(cartItemModel.startTime),
      endTime: cartItemModel.endTime ? LocalTime.parse(cartItemModel.endTime) : null,
      requestedTimes: cartItemModel.requestedTimes.map(timeframe => ({
        startTime: LocalTime.parse(timeframe.startTime),
        endTime: timeframe.endTime ? LocalTime.parse(timeframe.endTime) : null,
      })),
    }
  } catch {
    return undefined
  }
}

function mapCartItemToCartItemModel(cartItem: CartItem): CartItemModel {
  return {
    ...cartItem,
    recordingDate: cartItem.recordingDate.toString(),
    startTime: cartItem.startTime.toString(),
    endTime: cartItem.endTime?.toString() ?? null,
    requestedTimes: cartItem.requestedTimes.map(timeframe => ({
      startTime: timeframe.startTime.toString(),
      endTime: timeframe.endTime?.toString() ?? null,
    })),
  }
}
